import { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { ConnectedProps, connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Dialog,
  Button,
  Loader,
  Spinner,
  Table,
  TChangeEvent,
  ITableProps,
  Spacer,
} from '@oliasoft-open-source/react-ui-library';
import translations from 'src/internationalisation/translation-map.json';
import {
  selectCompareEstimatesDesigns,
  selectIsAddingCompareEstimates,
  selectIsFetchingCompareEstimates,
} from 'src/store/entities/compare-estimates/selectors';
import { getCompareData, updateCompare } from 'src/store/entities/compare-estimates/compare-estimates';
import { ICompare } from 'src/common/interfaces/compare-estimates.interfaces';
import { IDesignItem } from 'src/common/interfaces/campaigns.interfaces';
import type { TRootState } from 'src/store/store-types';
import { getFilteredData, getHeaderFilter, getHeaderLabels, getHeaderRow, getRows } from '../campaigns/utils/utils';

type TCompareModalProps = PropsFromRedux & {
  activeCompare: ICompare;
  setModalVisible: (param: boolean) => void;
  designs: IDesignItem[];
  isFetching: boolean;
  isAdding: boolean;
  subjectId: string;
};

const CompareModal = ({
  setModalVisible,
  activeCompare,
  designs,
  isFetching,
  isAdding,
  updateCompare,
  getCompareData,
}: TCompareModalProps) => {
  const { t } = useTranslation();
  const { company } = useParams();
  const [filters, setFilters] = useState<{ [key: string]: string }>({});
  const [selectedPage, setSelectedPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [designsValue, setDesignsValue] = useState<IDesignItem[]>(activeCompare.designs || []);

  useEffect(() => {
    if (company) {
      getCompareData(company);
    }
  }, []);
  const onClose = () => setModalVisible(false);
  const onSubmit = () => {
    updateCompare({
      ...activeCompare,
      designs: designsValue,
    });
    setModalVisible(false);
  };
  const headerLabels = getHeaderLabels(t, translations);
  const headerRow = getHeaderRow(headerLabels);
  const headerFilter = getHeaderFilter(headerLabels, filters, setFilters, t, translations, true, setSelectedPage);

  const startIndex = (selectedPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const filteredData = getFilteredData(designs, filters);
  const rows = getRows(filteredData, designsValue, setDesignsValue);
  const filteredRows = rows.slice(startIndex, endIndex);

  const table: ITableProps['table'] = {
    testId: 'choose-estimates-or-designs-modal-table',
    fixedWidth: '100%',
    headers: [{ cells: [{}, ...headerRow] }, { cells: [{}, ...headerFilter] }],
    rows: filteredRows,
    footer: {
      pagination: {
        rowCount: rows.length,
        selectedPage,
        rowsPerPage: {
          onChange: (evt: TChangeEvent) => setRowsPerPage(+evt.target.value),
          options: [
            { label: `10 / ${t(translations.page)}`, value: 10 },
            { label: `20 / ${t(translations.page)}`, value: 20 },
            { label: `50 / ${t(translations.page)}`, value: 50 },
          ],
          value: rowsPerPage,
        },
        onSelectPage: setSelectedPage,
        small: true,
      },
    },
  };

  return (
    <Modal visible centered onEscape={onClose}>
      <Dialog
        dialog={{
          heading: t(translations.campaigns_chooseEstimatesDesigns),
          width: 'auto',
          content: (
            <div
              style={{
                position: 'relative',
                margin: 'calc(var(--padding) * -1)',
              }}
            >
              {isFetching && (
                <Loader text={t(translations.fetching)} theme="light" cover>
                  <Spinner dark />
                </Loader>
              )}
              <form style={{ padding: 'var(--padding)' }}>
                <Table table={table} />
              </form>
            </div>
          ),
          footer: (
            <>
              <Button
                label={
                  designsValue?.length > 0
                    ? `${t(translations.confirmSelection)} (${designsValue?.length})`
                    : t(translations.confirmSelection)
                }
                testId="choose-estimates-or-designs-modal-button-confirm"
                colored
                onClick={onSubmit}
                disabled={isAdding || designsValue?.length < 2}
                tooltip={designsValue?.length < 2 ? t(translations.compare_minDesignsWarning) : ''}
              />
              <Spacer width="var(--padding-xs)" />
              <Button label={t(translations.cancel)} onClick={onClose} disabled={isAdding} />
            </>
          ),
          onClose,
        }}
      />
    </Modal>
  );
};

const mapStateToProps = (state: TRootState) => ({
  designs: selectCompareEstimatesDesigns(state),
  isFetching: selectIsFetchingCompareEstimates(state),
  isAdding: selectIsAddingCompareEstimates(state),
});

const mapDispatchToProps = {
  getCompareData,
  updateCompare,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = connector(CompareModal);

export { Container as CompareModal };
